import classNames from "classnames"
import Link from "next/link"
import { ReactNode } from "react"

import Icon, { IconName } from "@components/common/Icon"
import { queryAllElementsWithText, queryElementWithText } from "@services/queryHelper"
import { useTooltip } from "@services/tooltipHook"


/**
 * Queries for the first dashboard card with the given text.
 *
 * @param textToFind text to be used within a dashboard card
 * @returns dashboard card with the given text or undefined
 */
export const queryDashboardCard = (textToFind: string): HTMLElement =>
  // identificator for dashboard cards: the classname
  queryElementWithText(".dashboard-card", textToFind) as HTMLElement

/**
 * Queries for all dashboard cards with the given text or all cards, if textToFind is empty (default).
 *
 * @param textToFind text to be used within a dashboard card
 * @returns array of dashboard cards with the given text or undefined
 */
export const queryAllDashboardCards = (textToFind = ""): HTMLElement[] =>
  // identificator for dashboard cards: the classname
  queryAllElementsWithText(".dashboard-card", textToFind) as HTMLElement[]


interface IProps {
  children: ReactNode
  className?: string
  href?: string
  icon?: IconName
  large?: boolean
  onClick?: (...args: any) => void
  title: string
  tooltipText?: string
}

/**
 * This component provides a card for dashboards
 */
const DashboardCard: React.FC<IProps> = (props: IProps) => {
  const { children, href, icon, large = false, onClick, title, tooltipText = "", className = "" } = props

  const { tooltipTargetRef, ToolTip } = useTooltip(tooltipText)

  const finalClassName = classNames(
    "dashboard-card",
    className,
    { clickable: (!!href || !!onClick) },
    { "dashboard-card--large": large },
    "no-link-underline",
  )

  const contentBoxWithTooltip = <>
    <div>
      <div className="header">
        <h4>{icon && <Icon name={icon} size={22} />}{title}</h4>
      </div>
      <div className="dashboard-card__body">
        {children}
      </div>
    </div>
    {ToolTip}
  </>

  // finalclassname is bound onto every single variant instead of wrapping all possible
  // contentBox types within ONE div or span, to make sure, that the focusable element (a, div)
  // is presented with the className
  // to make sure, the tooltip visual is bound to the element as well as triggered when focussing the element
  return href
    ? <Link href={href}>
      <a ref={tooltipTargetRef} className={finalClassName}>{contentBoxWithTooltip}</a>
    </Link>
    : onClick
      // @todo: check: could this be a button?
      ? <div onClick={onClick} ref={tooltipTargetRef} className={finalClassName}>{contentBoxWithTooltip}</div>
      : <div ref={tooltipTargetRef} className={finalClassName}>{contentBoxWithTooltip}</div>
}

export default DashboardCard
